import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import { buildTtqWorkbenchSourceGroupItems } from '../../features/ttqib/buildTtqWorkbenchSourceGroupItems';
import { TtqWorkbenchSourceParticleGroup } from '../../features/ttqib/TtqWorkbenchSourceParticle';
import { TtqWorkbenchFocusLayout } from '../../features/ttqib/TtqWorkbenchSplitLayout';
import { TtqWorkbenchToolbar, TtqWorkbenchToolbarAction } from '../../features/ttqib/TtqWorkbenchToolbar';
import { useTtqWorkbenchFocusEmphasis } from '../../features/ttqib/useTtqWorkbenchFocusEmphasis';
import { TtqWorkbenchShell } from '../../features/ttqib/ttqWorkbenchShared';
import { parseTtqWorkbenchParticles } from '../../features/ttqib/ttqWorkbenchRoutes';
import { ttqService, type TtqSession } from '../../services/ttqService';

export function TtqSessionAggregateAWorkbenchPage() {
  const { ttqaId = '' } = useParams();
  const [searchParams] = useSearchParams();
  const particleIds = useMemo(() => parseTtqWorkbenchParticles(searchParams), [searchParams]);
  const [session, setSession] = useState<TtqSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    void ttqService.getSession(ttqaId)
      .then((s) => active && setSession(s))
      .catch((e) => active && setError(e instanceof Error ? e.message : '加载失败'))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [ttqaId]);

  const sourceEntries = useMemo(
    () => (session?.particles ?? []).filter((e) => particleIds.includes(e.particleId)),
    [particleIds, session?.particles],
  );
  const groupItems = useMemo(
    () => buildTtqWorkbenchSourceGroupItems(sourceEntries),
    [sourceEntries],
  );
  const hasResult = Boolean(session?.correlationAnalysis);
  const split = hasResult;
  const { emphasis, setEmphasis } = useTtqWorkbenchFocusEmphasis(split);

  const handleRun = async () => {
    if (!session) return;
    setRunning(true);
    setError(null);
    try {
      setSession(await ttqService.runSessionA(session.id));
    } catch (e) {
      setError(e instanceof Error ? e.message : '分析失败');
    } finally {
      setRunning(false);
    }
  };

  return (
    <TtqWorkbenchShell
      ttqaId={ttqaId}
      title="共同隐性能力挖掘"
      angleId="session_aggregate_a"
      loading={loading}
      error={error}
      restoreParticleIds={particleIds}
      toolbar={(
        <TtqWorkbenchToolbar
          showFocusToggle={split}
          leftLabel="源题"
          rightLabel="分析"
          emphasis={emphasis}
          onEmphasisChange={setEmphasis}
        >
          <TtqWorkbenchToolbarAction
            disabled={running || !session?.aggregationAvailable}
            loading={running}
            onClick={() => { void handleRun(); }}
          >
            运行共同隐性能力挖掘
          </TtqWorkbenchToolbarAction>
        </TtqWorkbenchToolbar>
      )}
    >
      <p className="text-xs text-muted">来源颗粒（{sourceEntries.length} 颗）</p>
      <TtqWorkbenchFocusLayout
        split={split}
        emphasis={emphasis}
        source={<TtqWorkbenchSourceParticleGroup items={groupItems} />}
        derived={hasResult ? (
          <section className="rounded-xl border border-border bg-surface p-4 shadow-sm">
            <p className="text-xs font-medium text-muted">分析结果</p>
            <p className="mt-2 whitespace-pre-wrap text-sm text-ink">
              {String((session!.correlationAnalysis as { summary?: string }).summary
                || JSON.stringify(session!.correlationAnalysis).slice(0, 2000))}
            </p>
          </section>
        ) : null}
      />
      {!hasResult ? (
        <p className="text-sm text-muted">运行后将在此展示共同隐性能力分析。</p>
      ) : null}
    </TtqWorkbenchShell>
  );
}
